/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */

import {useTranslation} from "react-i18next"
import DividerWithText from "../../../../../components/DividerWithText"
import CustomRadio from "../../../../../components/CustomRadio"
import ManageContractorsApp from "../../Functionality/ManageContractorsApp"
import {CustomDateRangePicker} from "../../../../../components/CustomDateRangePicker"
import {CustomSelect} from "../../../../../components/CustomSelect"
import {CustomTextField} from "../../../../../components/CustomTextField"
import CustomRadioGroup from "../../../../../components/CustomRadioGroup"
import React, {forwardRef} from 'react';
import {FREQUENCY_RENEWAL} from '../../../../../enums/FrequencyRenewalEnum';
import {USER_TYPE_MODEL} from '../../../../../enums/userTypeEnum';
import {
    FREQUENCY_VALUE_FIELD_NAME,
    getCurrentDay,
    RENEWAL_FIELD_NAME, START_DATE_FIELD_NAME,
    TIME_UNIT_FIELD_NAME
} from "../../Model/Models";
import {CustomDatePicker} from "../../../../../components/CustomDatePicker";
import {PERIOD_TIME_UNIT_ENUM} from "../../../../../enums/PeriodTimeUnitEnum";
import CustomUserChip from "../../../../../components/CustomUserChip";
import { permissionObj } from "../../../../../Helpers/permission"

const ManageContractors = forwardRef((props, ref) => {
    const {userType} = props;

    const {t} = useTranslation();
    const {
        config,
        onChange,
        setConfig,
        TIME_UNIT_OPTIONS_TRANS,
        txtLadDays,
        txtFrequencyValue,
        emptyFields, emptyFieldsCustom,
        isValidFrequency,
        isValidLeadDays,
        isValidLeadDaysByMonth,
        originalInfo,
        permission
    } = ManageContractorsApp({
        ref,
        userType
    });

    return (
        <div className="px-3">
            <DividerWithText title={t("documentation_renewal")} spacing="pb-3"/>

            <p className="font-color-shades-950 fs-14px ibl-font-w-600">
                {t("frequency_for_renewal")}
            </p>
            <CustomRadioGroup className="col-12">
                <CustomRadio
                    id="rbtn_mc_one_time"
                    trans="one_time_only"
                    value={FREQUENCY_RENEWAL.ONE_TIME_ONLY}
                    name={RENEWAL_FIELD_NAME}
                    checked={config.renewal === FREQUENCY_RENEWAL.ONE_TIME_ONLY}
                    onChange={onChange}
                    labelClassName="fs-11px"
                    disabled={!(permission?.includes(permissionObj?.WEB_CONTRACTOR_MANAGE_UPDATE) || permission?.includes(permissionObj?.WEB_SUPPLIER_MANAGE_UPDATE))}
                />

                <div className="row g-2 align-items-center col-12 p-0">
                    <div className="col-md-auto p-0 w-200px">
                        <CustomRadio
                            id="rbtn_mc_custom"
                            trans="custom_interval"
                            value={FREQUENCY_RENEWAL.CUSTOM_INTERVAL}
                            name={RENEWAL_FIELD_NAME}
                            checked={config.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL}
                            onChange={onChange}
                            labelClassName="fs-11px"
                             disabled={!(permission?.includes(permissionObj?.WEB_CONTRACTOR_MANAGE_UPDATE) || permission?.includes(permissionObj?.WEB_SUPPLIER_MANAGE_UPDATE))}
                        />
                    </div>
                    <div className="col-12 col-md-3 pl-0 ml-md-5 mb-1">
                        <CustomTextField
                            id="txt_mc_frequency_value"
                            trans="frequency_value"
                            type="number"
                            name={FREQUENCY_VALUE_FIELD_NAME}
                            value={config.frequencyValue}
                            onChange={onChange}
                            required={config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL}
                            disabled={config?.renewal === FREQUENCY_RENEWAL.ONE_TIME_ONLY || !(permission?.includes(permissionObj?.WEB_CONTRACTOR_MANAGE_UPDATE) || permission?.includes(permissionObj?.WEB_SUPPLIER_MANAGE_UPDATE))}
                            length={3}
                            placeholder={t("enter_a_number")}
                            isValidText={txtFrequencyValue}
                            isValid={isValidFrequency}
                            emptyFields={emptyFieldsCustom && config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL}
                        />
                    </div>
                    <div className="col-12 col-md-3 pl-0">
                        <CustomSelect
                            id="slt_mc_time_unit"
                            name={TIME_UNIT_FIELD_NAME}
                            keyName="key"
                            trans="time_unit"
                            required={config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL}
                            value={config?.timeUnit}
                            onChange={onChange}
                            list={TIME_UNIT_OPTIONS_TRANS}
                            showNoSelectionOption={false}
                            disabled={config?.renewal === FREQUENCY_RENEWAL.ONE_TIME_ONLY || !(permission?.includes(permissionObj?.WEB_CONTRACTOR_MANAGE_UPDATE) || permission?.includes(permissionObj?.WEB_SUPPLIER_MANAGE_UPDATE))}
                        />
                    </div>
                </div>
            </CustomRadioGroup>

            <p className="font-color-shades-950 fs-14px ibl-font-w-600 mb-2">
                {t("validity_period")}
            </p>

            { config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL &&
                <div className="row g-2 align-items-center">
                    <div className="col-12 col-md-auto d-flex align-items-center p-0 m-0 w-200px fs-12px">
                        <p className="mb-sm-3 mt-sm-0 mt-md-0 mt-3 mb-0">{t("start_date")}</p>
                    </div>
                    <div className="col-12 col-md-3 pl-0 ml-md-5">
                        <CustomDatePicker
                            id={"dp_mc_start_date"}
                            name={START_DATE_FIELD_NAME}
                            trans="date"
                            minDate={getCurrentDay()}
                            onChange={(value) =>
                                onChange({target: { name: START_DATE_FIELD_NAME, value}})
                            }
                            required={true}
                            emptyFields={emptyFields}
                            value={config?.startDate}
                            openTo={PERIOD_TIME_UNIT_ENUM.DAYS.key}
                            disabled={!(permission?.includes(permissionObj?.WEB_CONTRACTOR_MANAGE_UPDATE) || permission?.includes(permissionObj?.WEB_SUPPLIER_MANAGE_UPDATE))}
                        />
                    </div>
                </div>
            }

            <div className="row g-2 align-items-center">
                <div className="col-12 col-md-auto d-flex align-items-center p-0 m-0 w-200px fs-12px">
                    <p className="mb-sm-3 mt-sm-0 mt-md-0 mt-3 mb-0">{t("current_period_no_lead_time")}</p>
                </div>
                <div className="col-12 col-md-3 pl-0 ml-md-5">
                    <CustomDateRangePicker
                        id="dp_mc_custom"
                        disabled={config?.renewal !== FREQUENCY_RENEWAL.ONE_TIME_ONLY || !(permission?.includes(permissionObj?.WEB_CONTRACTOR_MANAGE_UPDATE) || permission?.includes(permissionObj?.WEB_SUPPLIER_MANAGE_UPDATE)) }
                        required={true}
                        trans="date_range"
                        value={config}
                        setRangeDate={setConfig}
                        endDateIndeterminate={config?.renewal === FREQUENCY_RENEWAL.ONE_TIME_ONLY}
                        textIndeterminate={t("forever")}
                        minDate={getCurrentDay()}
                        rangeLength={config?.frequencyValue}
                        rangeUnit={config?.timeUnit}
                    />
                </div>
            </div>

            { config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL &&
            parseInt(config?.frequencyValue) === parseInt(originalInfo?.frequencyValue) &&
            parseInt(config?.leadDays) === parseInt(originalInfo?.leadDays) &&
            config?.timeUnit === originalInfo?.timeUnit &&
            new Date(config?.startDate)?.getTime() === new Date(originalInfo?.startDate)?.getTime() &&
            (config?.startLeadAwareRange) &&
            <div className="row g-2 align-items-center mt-2">
                <div className="col-12 col-md-auto d-flex align-items-center p-0 m-0 w-200px fs-12px">
                    <p className="mb-sm-3 mt-sm-0 mt-md-0 mt-3 mb-0">{t("next_period_no_lead_time")}</p>
                </div>
                <div className="col-12 col-md-3 pl-0 ml-md-5">
                    <CustomDateRangePicker
                        id="dp_mc_custom"
                        disabled={true}
                        required={true}
                        trans="date_range"
                        value={{start: config?.startLeadAwareRange, end: config?.endLeadAwareRange}}
                        setRangeDate={setConfig}
                        textIndeterminate={t("forever")}
                        minDate={getCurrentDay()}
                        rangeLength={config?.frequencyValue}
                        rangeUnit={config?.timeUnit}
                    />
                </div>
            </div>
            }

            { config.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL &&
                <>
                    <p className="font-color-shades-950 fs-14px ibl-font-w-600 mt-3 mb-2">
                        {t("lead_days")}
                    </p>

                    <div className="row g-2 align-items-center">
                        <div className="col-12 col-md-auto d-flex align-items-center p-0 m-0 w-200px fs-12px">
                            <p className="mb-sm-3 mt-sm-0 mt-md-0 mt-3 mb-0">{`${t("upload_allowed_before")}`}</p>
                        </div>
                        <div className="col-12 col-md-3 pl-0 ml-md-5">
                            <CustomTextField
                                id="txt_mc_number_days_before"
                                type="number"
                                trans={"quantity"}
                                name="leadDays"
                                value={config.leadDays}
                                onChange={onChange}
                                required={true}
                                length={4}
                                placeholder={t("number_of_days_before_the_start_date")}
                                isValidText={txtLadDays}
                                isValid={isValidLeadDays && isValidLeadDaysByMonth}
                                emptyFields={emptyFields}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('days')}</p>}
                                disabled={!(permission?.includes(permissionObj?.WEB_CONTRACTOR_MANAGE_UPDATE) || permission?.includes(permissionObj?.WEB_SUPPLIER_MANAGE_UPDATE))}
                            />
                        </div>
                        { config?.timeUnit === PERIOD_TIME_UNIT_ENUM.MONTHS.name &&
                            <CustomUserChip
                                className="chip-primary-200-50 mt-0 justify-content-start mb-4"
                                name={t("for_monthly_intervals_legend")}
                                variant="large"
                            />
                        }
                    </div>
                </>
            }

            <DividerWithText title={t("deletion_policy")} spacing="py-3"/>
            <p className="font-color-shades-950 fs-14px ibl-font-w-600 mb-2">
                {userType === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE ?
                    t("inactive_contractor_retention") : t("inactive_supplier_retention")}
            </p>

            <div className="row g-2 align-items-center">
                <div className="col-12 col-md-auto d-flex align-items-start p-0 m-0 w-200px">
                    <p className="mb-sm-3 mt-sm-0 mt-md-0 mt-3 mb-0 font-color-shades-950 fs-12px ibl-font-w-400">
                        {userType === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE ?
                            t("set_the_number_of_days_before_an_inactive_contractor_is_deleted") : t("set_the_number_of_days_before_an_inactive_supplier_is_deleted")}
                    </p>
                </div>
                <div className="col-12 col-md-3 pl-0 ml-md-5">
                    <CustomTextField
                        id="txt_mc_wait_time"
                        type="number"
                        trans={"quantity"}
                        name="waitTime"
                        value={config?.waitTime}
                        onChange={onChange}
                        required
                        disabled={false}
                        length={3}
                        placeholder={t("number_of_days_before_the_start_date")}
                        emptyFields={emptyFields}
                         disabled={!(permission?.includes(permissionObj?.WEB_CONTRACTOR_MANAGE_UPDATE) || permission?.includes(permissionObj?.WEB_SUPPLIER_MANAGE_UPDATE))}
                    />
                </div>
            </div>

        </div>
    );
});

export default ManageContractors;
